// Header.js — Pro, minimal, blue-on-black header (no icons, no motion)
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { getUser, resetUserSession, getToken } from "../../service/AuthService";
import logo from "../../images/logo7.png";

function Header() {
  const { pathname } = useLocation();
  const user = getUser();
  const name = user !== "undefined" && user ? user.username : "";
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const menuItems = [
    { label: "Home", path: "/Home" },
    { label: "Dashboard", path: "/trading" },
    { label: "Live", path: "/Live-New" },
    { label: "Admin", path: "/Admin" },
  ];

  function handleLogout() {
    setIsLoggedIn(false);
    resetUserSession();
    window.location.href = "/Home";
  }

  useEffect(() => {
    const token = getToken();
    if (!token) return setIsLoggedIn(false);
    const exp = new Date(token.expirationTime).getTime();
    setIsLoggedIn(exp >= Date.now());
  }, []);

  return (
    <>
      <style>{`
        :root {
          --bg0: #0A0B0E;
          --bg1: #0B0D12;
          --ink: #EAF2FF;
          --muted: #9AA4B2;
          --stroke: #1E2330;
          --accent: #46C8FF;
          --accent-soft: #BFEAFF;
        }
        .modern-header {
          background: linear-gradient(180deg, var(--bg0), var(--bg1));
          border-bottom: 1px solid var(--stroke);
          padding: 0;
          position: sticky;
          top: 0;
          z-index: 1000;
        }
        .header-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 10px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }
        .header-logo img {
          width: 140px;
          height: auto;
          display: block;
        }

        .header-nav {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .nav-item {
          position: relative;
          padding: 8px 12px;
          color: #cfe9ff;
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
          border-radius: 8px;
          /* No motion/animation */
        }
        .nav-item:hover {
          background: rgba(255,255,255,0.04);
        }
        .nav-item.active {
          color: var(--accent);
          background: rgba(70, 200, 255, 0.10);
          border: 1px solid rgba(70, 200, 255, 0.30);
        }
        .nav-item.active::after {
          content: "";
          position: absolute;
          left: 12px;
          right: 12px;
          bottom: -10px;
          height: 2px;
          background: var(--accent);
          border-radius: 1px;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .user-chip {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 10px;
          border: 1px solid var(--stroke);
          background: rgba(255,255,255,0.04);
        }
        .avatar {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          color: #001018;
          font-size: 13px;
          font-weight: 800;
          background: var(--accent);
        }
        .user-name {
          color: var(--ink);
          font-size: 13px;
          max-width: 140px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .btn {
          border: 1px solid var(--stroke);
          background: #12151C;
          color: var(--ink);
          padding: 8px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }
        .btn:hover {
          background: #151925;
        }
        .btn.primary {
          border-color: #2a3546;
          background: #1a2230;
          color: var(--accent-soft);
        }
        .btn.primary:hover {
          background: #1d2736;
        }

        @media (max-width: 860px) {
          .header-container {
            padding: 12px 12px;
            gap: 10px;
          }
          .header-nav {
            gap: 4px;
          }
          .nav-item {
            padding: 6px 10px;
            font-size: 13px;
          }
          .nav-item.active::after {
            bottom: -8px;
          }
          .user-name { display: none; }
        }
      `}</style>

      <header className="modern-header">
        <div className="header-container">
          <div className="header-left">
            <div className="header-logo">
              <Link to="/" aria-label="Berbul.ai Home">
                <img src={logo} alt="Berbul.ai" />
              </Link>
            </div>
          </div>

          <nav className="header-nav" aria-label="Primary">
            {menuItems.map((item) => {
              const isActive = pathname === item.path;
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`nav-item ${isActive ? "active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="header-actions">
            {isLoggedIn ? (
              <>
                {name && (
                  <div className="user-chip" title={name}>
                    <div className="avatar">{name.charAt(0).toUpperCase()}</div>
                    <span className="user-name">{name}</span>
                  </div>
                )}
                <button className="btn" onClick={handleLogout}>Logout</button>
              </>
            ) : (
              <Link to="/Login">
                <button className="btn primary">Login</button>
              </Link>
            )}
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
